import { api } from '../lib/api.js';
import { el, roleLabel } from '../lib/dom.js';
import { confirmDialog, typedConfirm } from '../lib/ui.js';

// ---------------------------------------------------------------------------
// Account-wide actions — deactivate / reactivate / delete / restore the whole
// ConstructMind ACCOUNT. Shared by the Users directory and the user profile,
// the only two screens not framed by a single organisation.
//
// Delete and restore are confirmed by typing the email (the API re-checks it
// with assertConfirmedEmail), and every confirmation states how many
// organisations the action reaches — that number, not the row's Organisation
// column, is the true blast radius.
// ---------------------------------------------------------------------------

function scopeLine(u) {
  // 0 is a real answer: a removed-only account holds no live membership.
  const n = typeof u.activeOrgCount === 'number' ? u.activeOrgCount : 1;
  if (n > 1) return `They are a member of ${n} organisations. This closes ALL of them${u.orgName ? `, including ${u.orgName}` : ''}.`;
  if (n === 1) return 'This closes their membership everywhere and stops them signing in at all.';
  return 'They hold no live memberships. This closes the account itself, so they can no longer sign in.';
}

/**
 * Menu items for one account. u: { userId, email, fullName, accountActive,
 * accountDeletedAt, activeOrgCount, orgName? }. onDone(message, restored?)
 * runs after a successful action.
 */
export function accountActions(u, { onDone }) {
  const deleted = u.accountDeletedAt !== null && u.accountDeletedAt !== undefined;
  const items = [];

  // Only meaningful while the account exists: once deleted, isActive is
  // already false and the way back is "Restore account", not this.
  if (!deleted) {
    items.push(
      u.accountActive
        ? {
            label: 'Deactivate account',
            iconName: 'menu-deactivate',
            danger: true,
            title: `ACCOUNT-WIDE. Blocks ${u.email} from signing in to ANY organisation on their next request.`,
            onSelect: async () => {
              const ok = await confirmDialog({
                tone: 'amber',
                iconName: 'menu-deactivate',
                title: `Deactivate the account for ${u.email}?`,
                text:
                  'ACCOUNT-WIDE. They are blocked from signing in to ANY organisation on their next request. The account, its password, ' +
                  'and every organisation membership are kept exactly as they are — this only flips one switch, and one more click reverses it.',
                alert: { title: 'To block one organisation only', text: 'Open that organisation and use "Deactivate in org" instead.' },
                confirmLabel: 'Deactivate account',
                onConfirm: () => api.setUserAccountStatus(u.userId, false),
              });
              if (ok) onDone(`Account for ${u.email} deactivated.`);
            },
          }
        : {
            label: 'Reactivate account',
            iconName: 'check-circle',
            title: `ACCOUNT-WIDE. Lets ${u.email} sign in again, to every organisation their memberships still give them access to.`,
            onSelect: async () => {
              const ok = await confirmDialog({
                tone: 'green',
                iconName: 'check-circle',
                title: `Reactivate the account for ${u.email}?`,
                text: 'They can sign in again — to every organisation their memberships still give them access to. Nothing else changes.',
                confirmLabel: 'Reactivate account',
                onConfirm: () => api.setUserAccountStatus(u.userId, true),
              });
              if (ok) onDone(`Account for ${u.email} reactivated.`);
            },
          },
    );
  }

  items.push(
    deleted
      ? {
          label: 'Restore account',
          iconName: 'check-circle',
          title: `ACCOUNT-WIDE. Reactivates ${u.email} with their original password. Memberships are NOT restored.`,
          onSelect: async () => {
            let restored = null;
            const ok = await typedConfirm({
              tone: 'green',
              iconName: 'check-circle',
              title: `Restore the account for ${u.email}?`,
              text: 'The account becomes active again and can sign in with its original password, but its organisation memberships stay removed — add them back where they belong.',
              inputLabel: 'Type the email address to confirm',
              expected: u.email,
              caseInsensitive: true,
              confirmLabel: 'Restore account',
              onConfirm: async (typed) => {
                restored = await api.restoreUser(u.userId, typed.toLowerCase());
              },
            });
            if (ok) onDone(`Account for ${u.email} restored.`, restored);
          },
        }
      : {
          label: 'Delete account',
          iconName: 'modal-question',
          danger: true,
          title: `ACCOUNT-WIDE. Soft-deletes ${u.email}'s whole ConstructMind account.`,
          onSelect: async () => {
            const ok = await typedConfirm({
              tone: 'red',
              iconName: 'modal-question',
              title: `Delete the account for ${u.email}?`,
              text: `${scopeLine(u)}\n\nIt is a soft delete and can be undone with "Restore account" — which does NOT bring the memberships back.`,
              alert: {
                title: 'Not the same as removing them from one organisation',
                text: 'To take them out of one organisation only, open that organisation and use "Remove".',
              },
              inputLabel: 'Type the email address to confirm',
              expected: u.email,
              caseInsensitive: true,
              confirmLabel: 'Delete account',
              onConfirm: (typed) => api.deleteUser(u.userId, typed.toLowerCase()),
            });
            if (ok) onDone(`Account for ${u.email} deleted.`);
          },
        },
  );

  return items;
}

/** Shown after a restore: the memberships the deletion closed, so the operator knows what to put back. */
export function restoredPanel(r) {
  return el(
    'div',
    { class: 'result-panel' },
    el('h2', { class: 'result-panel__title' }, `${r.email} restored`),
    el(
      'p',
      { class: 'note note-info' },
      'The account is active again with its original password. Organisation memberships were not restored automatically — add them back from each organisation.',
    ),
    r.memberships.length === 0
      ? el('p', { class: 'note note-muted' }, 'This account held no organisation memberships when it was deleted.')
      : el('ul', { class: 'warn-list' }, r.memberships.map((m) => el('li', {}, `${m.orgName} — ${roleLabel(m.role)}`))),
  );
}
